// Content for the Communications page "L'analytique" section.
// All figures are illustrative demo data for the product interface.
export type Bi = { fr: string; en: string }

export const analyticsIntro = {
  eyebrow: { fr: 'L’analytique', en: 'Analytics' },
  line1: { fr: 'Voyez qui est réellement joint.', en: 'See who was actually reached.' },
  line2: { fr: 'Et ce qu’il faut faire ensuite.', en: 'And what to do next.' },
  body: {
    fr: [
      'Suivez la portée de vos communications en temps réel, comprenez les résultats par canal et par secteur,',
      'identifiez les non joints et obtenez des recommandations concrètes d’ATNA pour accroître l’efficacité de vos communications.',
    ],
    en: [
      'Track the reach of your communications in real time, understand results by channel and by sector,',
      'identify who wasn’t reached and get concrete recommendations from ATNA to maximize impact.',
    ],
  },
}

export const capabilityCards: { key: 'global' | 'territory' | 'topic' | 'atna'; title: Bi; body: Bi }[] = [
  { key: 'global', title: { fr: 'Performance globale', en: 'Overall performance' }, body: { fr: 'Taux de livraison, portée, interactions et satisfaction.', en: 'Delivery rate, reach, engagement and satisfaction.' } },
  { key: 'territory', title: { fr: 'Par territoire', en: 'By territory' }, body: { fr: 'Voyez quels secteurs sont joints — et où il reste des citoyens à informer.', en: 'See which sectors are reached — and where residents still need to be informed.' } },
  { key: 'topic', title: { fr: 'Par sujet', en: 'By topic' }, body: { fr: 'Repérez les sujets qui suscitent le plus d’interactions et de questions.', en: 'Identify the topics that generate the most engagement and questions.' } },
  { key: 'atna', title: { fr: 'Recommandations ATNA', en: 'ATNA recommendations' }, body: { fr: 'Des actions concrètes pour élargir la portée et joindre les personnes non jointes.', en: 'Concrete actions to improve reach and contact those not reached.' } },
]

export const dashboardNav: { key: string; label: Bi }[] = [
  { key: 'home', label: { fr: 'Tableau de bord', en: 'Dashboard' } },
  { key: 'users', label: { fr: 'Utilisateurs', en: 'Users' } },
  { key: 'id', label: { fr: 'ID', en: 'ID' } },
  { key: 'comms', label: { fr: 'Communications', en: 'Communications' } },
  { key: 'requests', label: { fr: 'Requêtes', en: 'Requests' } },
  { key: 'permits', label: { fr: 'Permis', en: 'Permits' } },
  { key: 'leisure', label: { fr: 'Loisirs', en: 'Recreation' } },
  { key: 'collect', label: { fr: 'Collectes', en: 'Waste collection' } },
  { key: 'meters', label: { fr: 'Compteurs d’eau', en: 'Water meters' } },
  { key: 'atna', label: { fr: 'IA ATNA', en: 'ATNA AI' } },
  { key: 'settings', label: { fr: 'Paramètres', en: 'Settings' } },
]

export const supportCard = {
  label: { fr: 'Soutien', en: 'Support' },
  body: { fr: 'Besoin d’aide avec le système de géociblage ?', en: 'Need help with the geotargeting system?' },
  link: { fr: 'Joindre le soutien technique', en: 'Contact support' },
}

export const kpis: { key: 'sent' | 'reached' | 'open' | 'interactions' | 'calls' | 'satisfaction'; label: Bi; value: Bi; sub: Bi; trend?: string }[] = [
  { key: 'sent', label: { fr: 'Avis envoyés', en: 'Notices sent' }, value: { fr: '125 842', en: '125,842' }, sub: { fr: 'par rapport au mois précédent', en: 'vs previous month' }, trend: '+18 %' },
  { key: 'reached', label: { fr: 'Citoyens joints', en: 'Residents reached' }, value: { fr: '91 %', en: '91%' }, sub: { fr: 'des citoyens inscrits', en: 'of registered residents' } },
  { key: 'open', label: { fr: 'Taux d’ouverture', en: 'Open rate' }, value: { fr: '73 %', en: '73%' }, sub: { fr: 'tous canaux', en: 'all channels' } },
  { key: 'interactions', label: { fr: 'Interactions citoyennes', en: 'Resident interactions' }, value: { fr: '12 487', en: '12,487' }, sub: { fr: 'clics, réponses, questions', en: 'clicks, replies, questions' } },
  { key: 'calls', label: { fr: 'Appels évités', en: 'Calls avoided' }, value: { fr: '2 134', en: '2,134' }, sub: { fr: 'estimés par ATNA', en: 'estimated by ATNA' } },
  { key: 'satisfaction', label: { fr: 'Satisfaction', en: 'Satisfaction' }, value: { fr: '4,7 / 5', en: '4.7 / 5' }, sub: { fr: 'communications', en: 'communications' } },
]

export const channelRows: { key: 'push' | 'sms' | 'email' | 'voice' | 'web' | 'social'; label: Bi; sent: Bi; open: number; interaction: number }[] = [
  { key: 'push', label: { fr: 'Push mobile', en: 'Mobile push' }, sent: { fr: '85 420', en: '85,420' }, open: 88, interaction: 24 },
  { key: 'sms', label: { fr: 'SMS', en: 'SMS' }, sent: { fr: '42 100', en: '42,100' }, open: 92, interaction: 18 },
  { key: 'email', label: { fr: 'Courriel', en: 'Email' }, sent: { fr: '112 500', en: '112,500' }, open: 45, interaction: 8 },
  { key: 'voice', label: { fr: 'Voix', en: 'Voice' }, sent: { fr: '8 400', en: '8,400' }, open: 75, interaction: 5 },
  { key: 'web', label: { fr: 'Web / Appli', en: 'Web / App' }, sent: { fr: '15 200', en: '15,200' }, open: 35, interaction: 12 },
  { key: 'social', label: { fr: 'Réseaux sociaux', en: 'Social media' }, sent: { fr: '—', en: '—' }, open: 22, interaction: 6 },
]

export const districts = [
  { key: 'A', color: '#4F46E5' },
  { key: 'B', color: '#F2A93B' },
  { key: 'C', color: '#22A04F' },
  { key: 'D', color: '#E23E3E' },
  { key: 'E', color: '#F2A93B' },
] as const

export const districtInfo = {
  name: { fr: 'Secteur A', en: 'Sector A' },
  lines: [
    { value: { fr: '12 450', en: '12,450' }, label: { fr: 'citoyens inscrits', en: 'registered residents' } },
    { value: { fr: '9 820', en: '9,820' }, label: { fr: 'cartes numériques actives', en: 'active Digital Cards' } },
    { value: { fr: '78 %', en: '78%' }, label: { fr: 'taux d’ouverture', en: 'open rate' } },
    { value: { fr: '45', en: '45' }, label: { fr: 'avis envoyés', en: 'notices sent' } },
  ],
}

export const topics: { label: Bi; open: string; clicks: string; questions: Bi }[] = [
  { label: { fr: 'Déneigement', en: 'Snow removal' }, open: '88', clicks: '34', questions: { fr: '1 240', en: '1,240' } },
  { label: { fr: 'Collecte des matières résiduelles', en: 'Waste collection' }, open: '75', clicks: '12', questions: { fr: '890', en: '890' } },
  { label: { fr: 'Travaux routiers', en: 'Roadwork' }, open: '82', clicks: '41', questions: { fr: '2 100', en: '2,100' } },
  { label: { fr: 'Restrictions d’eau', en: 'Water restrictions' }, open: '91', clicks: '22', questions: { fr: '450', en: '450' } },
  { label: { fr: 'Taxes municipales', en: 'Municipal taxes' }, open: '65', clicks: '18', questions: { fr: '1 850', en: '1,850' } },
]

export const analyticsReco = {
  title: { fr: 'ATNA recommande', en: 'ATNA recommends' },
  body: { fr: 'Relance vocale à 18 h 30 pour les 148 non joints du secteur A.', en: 'Voice follow-up at 6:30 PM for the 148 not reached in Sector A.' },
  action: { fr: 'Planifier la relance', en: 'Schedule the follow-up' },
}

export const analyticsSteps: { key: 'measure' | 'identify' | 'understand' | 'act'; title: Bi; body: Bi }[] = [
  { key: 'measure', title: { fr: 'Mesurez', en: 'Measure' }, body: { fr: 'Suivez la portée et les interactions en temps réel.', en: 'Track reach and engagement in real time.' } },
  { key: 'identify', title: { fr: 'Identifiez', en: 'Identify' }, body: { fr: 'Repérez les secteurs et les populations les moins bien joints.', en: 'Spot the sectors and groups that are hardest to reach.' } },
  { key: 'understand', title: { fr: 'Comprenez', en: 'Understand' }, body: { fr: 'Découvrez les sujets qui mobilisent le plus vos citoyens.', en: 'Discover the topics that engage your residents most.' } },
  { key: 'act', title: { fr: 'Agissez', en: 'Act' }, body: { fr: 'Recevez des recommandations concrètes d’ATNA pour accroître l’efficacité de vos communications.', en: 'Get concrete recommendations from ATNA to maximize impact.' } },
]
